import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import userHasPrivs from 'Helper/Auth/userHasPrivs'
import { useSelector } from 'react-redux'
import debug from 'Helper/debugHelpers'

const requiresPrivileges = () => {
  /*
   *  PrivConfig must be an object or array of objects that looks like:
   *  {
   *     context: dashboard, //(dashbaord | adminDisplay | patientDisplay)
   *     requiredPrivileges: [],
   *     privilegeOperator: 'or' //(or | and)
   *  }
   */
  const RequiresPrivileges = WrappedComponent => {
    return props => {
      const privObject = props.windowConfig?.context
      const windowName = props.windowConfig?.name
      const allPrivilegeObjects = useSelector(state => state.appConfig.privileges)
      const usersPrivileges = useSelector(state => state.auth.privilege)

      const userHasRequiredPrivs = useMemo(() => {
        if (privObject == null) {
          return false
        } else {
          return userHasPrivs(
            privObject.requiredPrivileges,
            {
              operator: privObject.privilegeOperator || 'or'
            },
            usersPrivileges
          )
        }
      }, [privObject, usersPrivileges])

      const generatePrivilegeError = useCallback((
        privObject,
        multiPrivilegeOperator
      ) => {
        let privName = 'ERROR'
        let category = 'Real Error'
        let xtractWindow = false
        if (Array.isArray(privObject?.requiredPrivileges)) {
          const privNameArr = []
          let privObj = {}
          // get all privileges' display names into privNameArr
          privObject.requiredPrivileges.forEach(priv => {
            if (priv === 'sys_admin') {
              xtractWindow = true
            } else {
              privObj = allPrivilegeObjects.filter(privObj => {
                return privObj.name === priv
              })
              if (privObj.length === 0) {
                privObj = [
                  {
                    display_name: '(unsupported privilege)',
                    category: 'Unsupported'
                  }
                ]
              }
              privNameArr.push(privObj[0].display_name)
            }
          })

          // join the display names with either 'and' or 'or' depending on what was passed in asWindow
          privName =
            multiPrivilegeOperator === 'and'
              ? privNameArr.join(' and ')
              : privNameArr.join(' or ')
          category = privObj[0].category
        } else if (privObject?.requiredPrivileges == null) {
          privName = '(Invalid privilege object)'
          category = 'Unsupported'
          debug.log(
            `Unsupported privilege object in ${windowName}. Missing requiredPrivileges property`,
            debug.logLevels.AUTH,
            'error'
          )
        } else {
          const privObj = allPrivilegeObjects.filter(privObj => {
            return privObj.name === privObject.requiredPrivileges
          })
          if (privObj.length === 0) {
            privName = '(unsupported privilege)'
            category = 'Unsupported'
          } else {
            category = privObj[0].category
            privName = privObj[0].display_name
          }
        }
        const adminSentence = 'This window is for Xtract Solution use only'
        const normalSentence = `The ${privName} privilege within ${category} is required to perform this action`
        return xtractWindow ? adminSentence : normalSentence
      }, [allPrivilegeObjects, userHasRequiredPrivs])

      const privilegeErrorMessage = useMemo(() => {
        if (!userHasRequiredPrivs || privObject?.requiredPrivileges == null) {
          return generatePrivilegeError(
            privObject,
            privObject?.multiPrivilegeOperator || 'or'
          )
        } else {
          return null
        }
      }, [privObject, generatePrivilegeError, userHasRequiredPrivs])

      return (
        <WrappedComponent
          {...props}
          hasPrivileges={userHasRequiredPrivs}
          privilegeErrorMessage={privilegeErrorMessage}
        />
      )
    }
  }
  return RequiresPrivileges
}

export default requiresPrivileges

requiresPrivileges.propTypes = {
  auth: PropTypes.object.isRequired,
  privConfigArg: PropTypes.oneOf([PropTypes.array, PropTypes.object]).isRequired,
  allPrivilegeObjects: PropTypes.array.isRequired
}
